import { Link } from "react-router-dom";
import {
  Box,
  Image,
  Icon,
  Flex,
  useMediaQuery,
  Button,
  useDisclosure,
  DarkMode,
  forwardRef,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { useAuth, useMeeting, useProfile } from "@hooks";
import { useLeaveWebMeetingMutation } from "@store/api/meeting";
import {
  isWebUser,
  isZoomApp,
  generateUrlWithQueryParams,
} from "@utils/helpers";
import {
  NavbarDropdown,
  LeaveMeetingModal,
  HelpModal,
  AttendeeAndHostToggle,
} from "@components";
import WebMeetingCodeEntry from "@pages/meeting/WebMeetingCodeEntry";
import { $Routes } from "@routes";
import { removeWebUserFromMeeting } from "@store/customActions";
import townhallLogoOnly from "@assets/townhall-logo-only.svg";
import townhallText from "@assets/townhall_text.svg";
import { IoHelpCircle } from "react-icons/io5";

const Navbar = forwardRef(({ _ }, ref) => {
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  // TODO: Refactor to have only one useDisclosure and modal to display help and leave meeting
  const {
    isOpen: isLeaveMeetingOpen,
    onOpen: onOpenLeaveMeeting,
    onClose: onCloseLeaveMeeting,
  } = useDisclosure();
  const {
    isOpen: isHelpOpen,
    onOpen: onOpenHelp,
    onClose: onCloseHelp,
  } = useDisclosure();
  const {
    isOpen: isJoinMeetingOpen,
    onOpen: onOpenJoinMeeting,
    onClose: onCloseJoinMeeting,
  } = useDisclosure();
  const dispatch = useDispatch();
  const { profile } = useAuth();
  const { isAuthenticated } = useProfile();
  const {
    isMeetingActive,
    platformMeetingInstanceId,
    isHostOrCoHost,
    platform,
  } = useMeeting();
  const [leaveWebMeeting] = useLeaveWebMeetingMutation();

  const homeUrl = $Routes.rootPage;

  const leaveMeeting = async () => {
    if (platform === "WEB") {
      // Use new web meeting leave endpoint
      try {
        await leaveWebMeeting().unwrap();
        onCloseLeaveMeeting();
      } catch (error) {
        console.error("Failed to leave web meeting:", error);
        onCloseLeaveMeeting();
      }
    } else {
      // Keep existing Zoom leave logic
      dispatch(
        removeWebUserFromMeeting({
          platformMeetingInstanceId,
          userId: profile?._id,
        }),
      );
      onCloseLeaveMeeting();
    }
  };

  return (
    <Box
      bgColor="dark.800"
      padding="0.5rem"
      boxShadow="0px 5px 140px rgba(0, 12, 59, 0.2)"
      position="static"
      ref={ref}
    >
      <LeaveMeetingModal
        isOpen={isLeaveMeetingOpen}
        handleClose={onCloseLeaveMeeting}
        handleLeaveMeeting={leaveMeeting}
      />
      <HelpModal isOpen={isHelpOpen} handleClose={onCloseHelp} />
      <WebMeetingCodeEntry
        isOpen={isJoinMeetingOpen}
        onClose={onCloseJoinMeeting}
      />
      <Flex maxWidth="9xl" justifyContent="space-between" alignItems="center">
        <Link to={homeUrl}>
          <Flex>
            <Image
              src={townhallLogoOnly}
              alt="Townhall"
              width={isMobile ? 12 : 14}
            />
            {!isMobile && <Image src={townhallText} alt="Townhall" />}
          </Flex>
        </Link>
        <Flex alignItems="center" justify="space-around" width="30">
          {isAuthenticated && isWebUser(profile) && !isMeetingActive ? (
            <>
              <DarkMode>
                <Button
                  variant="primary"
                  mr={3}
                  onClick={onOpenJoinMeeting}
                  data-testid="join-web-meeting-navbar-button"
                >
                  Join Meeting
                </Button>
              </DarkMode>
              <DarkMode>
                {/* TODO: Comment out the Help button for now because it's taking space on Mobile */}
                {/* <Button
                  leftIcon={
                    <Icon as={IoHelpCircle} color="dark.200" boxSize="5" />
                  }
                  variant="secondary"
                  mr={3}
                  onClick={onOpenHelp}
                >
                  Help
                </Button> */}
              </DarkMode>
            </>
          ) : null}
          {isWebUser(profile) &&
          isMeetingActive &&
          !isHostOrCoHost(profile?._id) ? (
            <Button
              variant="error"
              mr={3}
              onClick={onOpenLeaveMeeting}
              data-testid="leave-web-meeting"
            >
              Leave
            </Button>
          ) : null}
          {isAuthenticated ? (
            <AttendeeAndHostToggle isHostOrCoHost={isHostOrCoHost} />
          ) : null}
          {isAuthenticated ? (
            <Flex p={1} bg="purple.700" borderRadius="full">
              <NavbarDropdown user={profile} />
            </Flex>
          ) : (
            <Flex alignItems="center" gap={2}>
              <Button
                as={Link}
                to={$Routes.loginPage}
                variant="ghost"
                size="sm"
                color="whiteAlpha.800"
                fontWeight="500"
                _hover={{ color: "white", bg: "whiteAlpha.100" }}
              >
                Log in
              </Button>
              <DarkMode>
                <Button
                  as={Link}
                  to={$Routes.registerPage}
                  variant="primary"
                  size="sm"
                >
                  Register
                </Button>
              </DarkMode>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Box>
  );
});

export default Navbar;
